/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useEffect, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftSelect from "components/SoftComponents/SoftSelect";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftPagination from "components/SoftComponents/SoftPagination";

// Soft UI Dashboard PRO React example components
import RevisedTableHeadCell from "./RevisedTableHeadCell";
import RevisedTableBodyCell from "./RevisedTableBodyCell";
import { Accordion, AccordionDetails, AccordionSummary, Card, Grid } from "@mui/material";
import SoftButton from "components/SoftComponents/SoftButton";
import { useSoftUIController } from "hooks/context";

function RevisedTable({
  typeOptions = false,
  types = false,
  entriesPerPage = { defaultValue: 20, entries: [20, 40, 60, 80] },
  canSearch = false,
  showTotalEntries = true,
  showAdvancedDeviceLevel,
  table,
  pagination = { variant: "gradient", color: "dark" },
  isSorted = true,
  noEndBorder = false,
  noBackground = false,
  onRowClicked = false,
  canAdd = { state: false, func: {} },
  children,
  header = null,
}) {
  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 20;
  const entries = entriesPerPage.entries ? entriesPerPage.entries : [20, 40, 60, 80];
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable({
    columns,
    data,
    autoResetHiddenColumns: false,
    autoResetSortBy: false,
    autoResetGlobalFilter: false,
    initialState: {
      pageIndex: 0
    }
  },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter }
  } = tableInstance;

  // Set the default value for the entries per page when component mounts
  useEffect(() => setPageSize(defaultValue || 20), [defaultValue]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = ({ value }) => setPageSize(value);

  const [controller] = useSoftUIController();
  const { deviceLayout } = controller;

  const [tableSearchExpanded, setTableSearchExpanded] = useState(false);

  // Render the paginations
  const renderPagination = pageOptions.map((option) => (
    <SoftPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </SoftPagination>
  ));

  const getTableHeader = () => {
    const content = (
      <>
        <SoftBox>{header}</SoftBox>
        <Grid container spacing={{ xxs: 1, xl: 2 }} alignItems="center">
          {typeOptions && (
            <Grid item xxs={8} md={3}>
              <SoftBox mb={0.5} pl={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">Category</SoftTypography>
              </SoftBox>
              <SoftBox sx={{ borderRadius: '10px', background: '#fff', width: '100%' }}>
                <SoftSelect
                  size="medium"
                  value={types.state}
                  options={typeOptions}
                  onChange={(selected) => types.func(selected?.value)}
                  placeholder="Select a category..."
                />
              </SoftBox>
            </Grid>
          )}
          {entriesPerPage && (
            <Grid item xxs={canAdd.state && !typeOptions ? 6 : 4} md={!canAdd?.state && !typeOptions ? 5 : !(canAdd.state && !!typeOptions) ? 4 : (canAdd.state && typeOptions ? 2 : 4)}>
              <SoftBox mb={0.5} pl={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">Show per Page</SoftTypography>
              </SoftBox>
              <SoftBox sx={{ borderRadius: '10px', background: '#fff', width: '100%' }}>
                <SoftSelect
                  size="medium"
                  defaultValue={{ value: defaultValue, label: defaultValue }}
                  options={entries.map((entry) => ({ value: entry, label: entry }))}
                  onChange={setEntriesPerPage}
                  placeholder="Select a category..."
                  sx={{ width: 'unset !important' }}
                />
              </SoftBox>
            </Grid>
          )}
          {(canAdd.state && !typeOptions) && (
            <Grid item xxs={6} md={0} display={{ xxs: "flex", md: 'none' }} justifyContent="flex-end" alignItems="flex-end">
              <SoftBox mt={3} width="100%">
                <SoftButton variant="gradient" color="dark" fullWidth onClick={canAdd.func} sx={{ maxHeight: '40px !important' }}>
                  <Icon sx={{ mr: 1 }}>add</Icon> New
                </SoftButton>
              </SoftBox>
            </Grid>
          )}
          {canSearch && (
            <Grid item xxs={canAdd.state && !typeOptions ? 12 : 8} md={!canAdd?.state && !typeOptions ? 7 : !(canAdd.state && !!typeOptions) ? 6 : (canAdd.state && typeOptions ? 5 : 5)}>
              <SoftBox mb={0.5} pl={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">Search Entries</SoftTypography>
              </SoftBox>
              <SoftBox sx={{ background: '#fff', borderRadius: '0.5rem' }}>
                <SoftInput
                  placeholder="Search input or output..."
                  icon={{ component: "search", direction: "left" }}
                  value={search}
                  onChange={({ currentTarget }) => {
                    setSearch(search);
                    onSearchChange(currentTarget.value);
                  }}
                  sx={({ typography: { fontFamily } }) => ({
                    fontFamily: fontFamily
                  })}
                />
              </SoftBox>
            </Grid>
          )}
          {canAdd.state && (
            <Grid item xxs={4} md={2} display={{ xxs: (canAdd.state && !typeOptions) ? "none" : "flex", md: 'flex' }} justifyContent="flex-end" alignItems="flex-end">
              <SoftBox mt={3} width="100%">
                <SoftButton variant="gradient" color="dark" fullWidth onClick={canAdd.func} sx={{ maxHeight: '40px !important' }}>
                  <Icon sx={{ mr: 1 }}>add</Icon> New
                </SoftButton>
              </SoftBox>
            </Grid>
          )}
        </Grid>
      </>
    );

    if (deviceLayout == "mobile") {
      return (
        <Accordion onClick={() => setTableSearchExpanded(!tableSearchExpanded)} sx={({ functions: { rgba } }) => ({ margin: '0 !important', boxShadow: 0, borderRadius: '16px 16px 0 0', backgroundColor: noBackground ? rgba("#fff", 1) : rgba("#dcdcdc", 1), borderBottom: "1px solid #8e98a5" })}>
          <AccordionSummary sx={{ minHeight: '32px !important', px: 1.5, '> .MuiAccordionSummary-content': { margin: '0 !important' } }}>
            <SoftBox display="flex" justifyContent="flex-start" alignItems="center" gap={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">Table Search</SoftTypography>
            <Icon sx={{ display: 'inline-block' }} color="secondary">{!tableSearchExpanded ? 'expand_less' : 'expand_more'}</Icon>
            </SoftBox>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: '8px !important' }}>
            {content}
          </AccordionDetails>
        </Accordion>
      );
    }
    else {
      return (
        <SoftBox py={1} px={noBackground ? 0 : 2} sx={({ functions: { rgba } }) => ({ borderRadius: '16px 16px 0 0', backgroundColor: noBackground ? rgba("#fff", 1) : rgba("#dcdcdc", 1), borderBottom: "1px solid #8e98a5" })}>
          {content}
        </SoftBox>
      );
    }
  }

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) =>
    value > pageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }) => gotoPage(Number(value.value - 1));

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  // Setting the entries starting point
  const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

  // Setting the entries ending point
  let entriesEnd;

  if (pageIndex === 0) {
    entriesEnd = pageSize;
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }

  return (
    <TableContainer sx={{ boxShadow: "none", height: 'inherit' }}>
      <SoftBox display="flex" flexDirection="column" borderRadius="16px" color={"white"} width="100%" height="100%"
        sx={({ functions: { rgba } }) => ({
          backgroundColor: noBackground ? rgba("#fff", 1) : rgba("#eee", 1),
          overflow: "hidden", // Clips any horizontal bleed
          minWidth: 0,        // Allows the box to shrink to fit the screen
          width: '100%'
        })}
      >
        {getTableHeader()}
        <SoftBox height="auto" sx={{ overflowY: 'auto' }} px={{ xxs: noBackground ? 0 : 1, xl: noBackground ? 0 : 2 }} pt={{ xxs: 1, xl: 2 }} flex={1}>
          {children != null ? (
            children
          ) : (
            <>
              <Card sx={{ boxShadow: "none", border: "1px solid #e0e0e0" }}>
                <SoftBox p={0} sx={{ overflowX: "auto" }}>
                  <Table {...getTableProps()}>
                    <SoftBox component="thead" sx={{ borderBottom: '2px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>
                      {headerGroups.map((headerGroup) => {
                        const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();

                        return (
                          <TableRow key={headerGroupKey} {...headerGroupProps}>
                            {headerGroup.headers.map((column, key) => {
                              const { key: headerKey, ...headerProps } = column.getHeaderProps(isSorted && column.getSortByToggleProps());

                              return (
                                <RevisedTableHeadCell
                                  key={headerKey}
                                  {...headerProps}
                                  width={column.width ? column.width : "auto"}
                                  align={column.align ? column.align : "left"}
                                  sorted={setSortedValue(column)}
                                >
                                  {column.render("Header")}
                                </RevisedTableHeadCell>
                              )
                            })}
                          </TableRow>
                        )
                      })}
                    </SoftBox>
                    <TableBody {...getTableBodyProps()}>
                      {page.map((row) => {
                        prepareRow(row);
                        const { key: rowKey, ...getRowProps } = row.getRowProps();

                        return (
                          <TableRow key={rowKey} {...getRowProps} sx={{ borderBottom: '1px solid #e0e0e0', '&:hover': { backgroundColor: '#f8f9fa' } }}>
                            {row.cells.map((cell) => {
                              const { key: cellKey, ...cellProps } = cell.getCellProps();

                              return (
                                <RevisedTableBodyCell
                                  key={cellKey}
                                  {...cellProps}
                                  noBorder={noEndBorder && rows.length - 1 === key}
                                  align={cell.column.align ? cell.column.align : "left"}
                                  weight={cell.column?.weight || "normal"}
                                  showAdvancedDeviceLevel={showAdvancedDeviceLevel}
                                  onRowClicked={onRowClicked}
                                >
                                  {cell.render("Cell")}
                                </RevisedTableBodyCell>
                              )
                            })}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </SoftBox>
              </Card>
              <SoftBox
                display="flex"
                flexDirection={{ xxs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={"center"}
                p={!showTotalEntries && pageOptions.length === 1 ? 0 : 2}
              >
                {showTotalEntries && (
                  <SoftBox mb={{ xxs: 3, sm: 0 }}>
                    <SoftTypography variant="button" color="secondary" fontWeight="regular">
                      Showing {entriesStart} to {entriesEnd > rows.length ? rows.length : entriesEnd} of {rows.length} entries
                    </SoftTypography>
                  </SoftBox>
                )}
                {pageOptions.length > 1 && (
                  <SoftPagination
                    variant={pagination.variant ? pagination.variant : "gradient"}
                    color={pagination.color ? pagination.color : "info"}
                  >
                    {canPreviousPage && (
                      <SoftPagination item onClick={() => previousPage()}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                      </SoftPagination>
                    )}
                    {renderPagination.length > 6 ? (
                      <SoftBox width="5rem" mx={1}>
                        <SoftInput
                          inputProps={{ type: "number", min: 1, max: customizedPageOptions.length }}
                          value={customizedPageOptions[pageIndex]}
                          onChange={(handleInputPagination, handleInputPaginationValue)}
                          sx={({ typography: { fontFamily } }) => ({
                            fontFamily: fontFamily
                          })}
                        />
                      </SoftBox>
                    ) : (
                      renderPagination
                    )}
                    {canNextPage && (
                      <SoftPagination item onClick={() => nextPage()}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                      </SoftPagination>
                    )}
                  </SoftPagination>
                )}
              </SoftBox>
            </>
          )}
        </SoftBox>
      </SoftBox>
    </TableContainer>
  );
}

// Typechecking props for the RevisedTable
RevisedTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  types: PropTypes.oneOfType([
    PropTypes.shape({
      state: PropTypes.any,
      func: PropTypes.func,
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  typeOptions: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.any,
    }),
    PropTypes.bool,
  ]),
  canAdd: PropTypes.shape({
    state: PropTypes.bool,
    func: PropTypes.func,
  }),
  showTotalEntries: PropTypes.bool,
  showAdvancedDeviceLevel: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  noBackground: PropTypes.bool,
  onRowClicked: PropTypes.func,
};

export default RevisedTable;
